import { useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import { useGetProjects } from '@/entities/project';
import { useCurrentSpaceId, useSpaceCredits } from '@/entities/teamspace';

import { API } from '@/shared/api';
import { PATHS } from '@/shared/constants/routes/paths';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { pollLinkedMediaUploadStatus } from '@/shared/lib/pollLinkedMediaUploadStatus';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';

import { consumeLandingFlow, landingFlowEnabled, saveLandingProgressFloor } from './landingFlowStorage';
import { type DubLandingFlowData, LandingFlowKind } from './types';

const pollUploadUntilReady = async (mediaId: string): Promise<void> => {
  const status = await pollLinkedMediaUploadStatus(mediaId, {
    onStatusCheckError: (error) => {
      console.error('Error checking upload status:', error);
    },
  });

  if (status === 'failed') {
    throw new Error('Video upload failed');
  }

  if (status === 'timeout') {
    throw new Error('Video upload timed out');
  }
};

const resolveMediaLink = async (data: DubLandingFlowData): Promise<string> => {
  if ('media_link' in data) return data.media_link;

  const landingMedia = await API.getLandingMedia(data.media_id);
  return landingMedia.url;
};

export const useLandingFlow = () => {
  const navigate = useNavigate();
  const spaceId = useCurrentSpaceId();
  const { mutateAsync: createProject } = useMutation({
    mutationFn: API.createProject,
  });
  const { refetch: refetchCredits } = useSpaceCredits();
  const { refetch: refetchProjects } = useGetProjects({ enabled: false });

  const [isLandingFlow, setIsLandingFlow] = useState(landingFlowEnabled);
  const [landingProgress, setLandingProgress] = useState(1);

  const handleVideoTranscriptFlow = useCallback(
    async (data: DubLandingFlowData) => {
      try {
        setLandingProgress(5);
        const mediaLink = await resolveMediaLink(data);

        setLandingProgress(10);
        const libraryMedia = await API.uploadVideoByLink({ link: mediaLink });

        setLandingProgress(14);
        await pollUploadUntilReady(libraryMedia.id);

        setLandingProgress(18);
        const project = await createProject({
          name: '',
          dst_lang: data.target_lang,
          prompt: null,
          space_id: spaceId,
          video_id: libraryMedia.id,
        });

        setLandingProgress(20);
        saveLandingProgressFloor(20);

        await refetchProjects();
        navigate(`/${PATHS.spaces}/${spaceId}/${PATHS.project}/${project.id}`);

        await refetchCredits();
      } catch (error) {
        showErrorAlert(error);
      } finally {
        setIsLandingFlow(false);
      }
    },
    [createProject, navigate, refetchCredits, refetchProjects, spaceId],
  );

  useExecuteOneTime(() => {
    const data = consumeLandingFlow();

    if (!data) return setIsLandingFlow(false);

    if (data.kind === LandingFlowKind.videoTranscript) {
      setIsLandingFlow(true);
      void handleVideoTranscriptFlow(data);
    }
  });

  return { isLandingFlow, landingProgress };
};
